import LocaleFormat from './LocaleFormat.js';

/**
 * Utilidades para exportar gráficos ECharts a PNG, incluyendo el título y la
 * fecha de generación ("datos al DD/MM/YYYY") horneados en la imagen.
 *
 * Compartido por el gráfico de ERIC (EricChart) y los gráficos de dashboards
 * de la web (Radial2, NightingaleRoseLabels).
 */

// pixelRatio del export: 2 = imagen nítida (retina). El canvas de composición
// trabaja en ese mismo espacio de píxeles, así que las fuentes se escalan por él.
var PIXEL_RATIO = 2;

/**
 * Formatea una fecha al estilo del locale (DD/MM/YYYY en es). Si no se pasa
 * fecha (o es inválida), cae a la fecha de hoy.
 */
function formatearFecha(fecha) {
  var f = fecha ? LocaleFormat.formatDate(fecha) : '';
  if (!f) {
    f = LocaleFormat.formatDate(new Date());
  }
  return f;
}

/**
 * Construye el caption "datos al {fecha}". Usa la fecha del dato si se pasa;
 * si no, la fecha de hoy (fecha de generación).
 */
export function captionDatosAl(fecha) {
  return 'datos al ' + formatearFecha(fecha);
}

/**
 * Nombre de archivo seguro a partir de un título (sin extensión).
 */
export function nombreArchivoDesdeTitulo(titulo, fallback) {
  var base = (titulo || '').replace(/[^a-zA-Z0-9_\-\s]/g, '').trim().replace(/\s+/g, '_');
  return base || (fallback || 'grafico');
}

/**
 * Dispara la descarga de un PNG a partir de una instancia de ECharts,
 * componiendo el gráfico con un título arriba y el caption de fecha abajo.
 *
 * @param {object} echartsInstance  instancia de ECharts (getEchartsInstance()).
 * @param {object} opciones         { titulo, caption, nombreArchivo }
 */
export function descargarChartPng(echartsInstance, opciones) {
  opciones = opciones || {};
  if (!echartsInstance || typeof echartsInstance.getDataURL !== 'function') {
    return;
  }

  var titulo = (opciones.titulo || '').trim();
  var caption = (opciones.caption || '').trim();
  var nombreArchivo = opciones.nombreArchivo || 'grafico';
  var ratio = PIXEL_RATIO;

  var src = echartsInstance.getDataURL({
    type: 'png',
    pixelRatio: ratio,
    backgroundColor: '#ffffff',
  });

  var img = new Image();
  img.onload = function() {
    var padX = 16 * ratio;
    var titleH = titulo ? 34 * ratio : 10 * ratio;
    var capH = caption ? 28 * ratio : 10 * ratio;

    var canvas = document.createElement('canvas');
    canvas.width = img.width;
    canvas.height = img.height + titleH + capH;

    var ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Fondo blanco (el PNG de ECharts puede venir con transparencia en zonas).
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    // Título centrado arriba.
    if (titulo) {
      ctx.fillStyle = '#333333';
      ctx.font = 'bold ' + (15 * ratio) + 'px sans-serif';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(titulo, canvas.width / 2, titleH * 0.6, canvas.width - 2 * padX);
    }

    // Gráfico.
    ctx.drawImage(img, 0, titleH);

    // Caption de fecha abajo a la derecha.
    if (caption) {
      ctx.fillStyle = '#888888';
      ctx.font = (11 * ratio) + 'px sans-serif';
      ctx.textAlign = 'right';
      ctx.textBaseline = 'middle';
      ctx.fillText(caption, canvas.width - padX, titleH + img.height + capH * 0.55);
    }

    var disparar = function(href, revocar) {
      var link = document.createElement('a');
      link.href = href;
      link.setAttribute('download', nombreArchivo + '.png');
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      if (revocar) {
        URL.revokeObjectURL(href);
      }
    };

    if (canvas.toBlob) {
      canvas.toBlob(function(blob) {
        if (!blob) {
          disparar(canvas.toDataURL('image/png'), false);
          return;
        }
        disparar(URL.createObjectURL(blob), true);
      }, 'image/png');
    } else {
      disparar(canvas.toDataURL('image/png'), false);
    }
  };
  img.src = src;
}

export default { descargarChartPng: descargarChartPng, captionDatosAl: captionDatosAl, nombreArchivoDesdeTitulo: nombreArchivoDesdeTitulo };
